import React, {Fragment, useState} from "react";
import {GaragePriceCalculation, LabourStep, Part, Service} from "@App/types";
import {useMoneyFormat} from "@App/hooks/useMoneyFormat";
import {GarageListItemBonuses} from "@App/Components/GarageListItemBonuses";
import {Modal} from "@App/Components/Modal";
import {CogIcon, LabourStepsIcon} from "@App/Components/common/Icons";
import {Hr} from "@App/Components/Hr";
import {ChevronRightIcon, InformationCircleIcon} from "@heroicons/react/outline";
import {useSelector} from "react-redux";
import {State} from "@App/state";
import {useTranslation} from "react-i18next";


type GarageListItemPriceProps = {
    calculation: GaragePriceCalculation
    priceIsFixed?: boolean
    labour?: LabourStep[]
    parts?: Part[]
    service: Service
};

export const GarageListItemPrice = React.memo<GarageListItemPriceProps>(({
                                                                             priceIsFixed,
                                                                             parts,
                                                                             labour,
                                                                             calculation,
                                                                             service
                                                                         }: GarageListItemPriceProps) => {
    const {t} = useTranslation()
    const {formatMoney} = useMoneyFormat()
    const [calculationDetailsOpened, setCalculationDetailsOpened] = useState(false)
    const organization = useSelector((state: State) => state.orders.organization)

    const priceName = priceIsFixed ? "Preis" : "Kostenvoranschlag"

    return (
        <div>
            {(!calculation.promotion || (calculation.promotion && calculation.promotion.type === 'bonus')) && (
                <div
                    className="flex justify-between gap-3 items-center text-lg text-success font-bold">
                    <div className="text-base">{priceName}</div>
                    <div>
                        {!!calculation.estimated_price && (
                            <Fragment>
                                € <span className="text-3xl">{formatMoney(calculation.estimated_price)}</span>
                            </Fragment>
                        )}
                        {!calculation.estimated_price && (
                            <span className="text-lg">Vor Ort</span>
                        )}
                    </div>
                </div>
            )}

            {calculation.promotion && calculation.promotion.type !== 'bonus' && (
                <Fragment>
                    <div className="flex justify-between gap-3 mb-1 font-bold">
                        <div>{priceName}</div>
                        <div
                            className={calculation.estimated_price_before_promotion > calculation.estimated_price ? 'line-through' : 'hidden'}>
                            {calculation.estimated_price_before_promotion && calculation.estimated_price
                                ? '€ ' + formatMoney(calculation.estimated_price_before_promotion)
                                : 'Vor Ort'
                            }
                        </div>
                    </div>
                    <div className="flex justify-between items-center text-lg text-success font-bold">
                        <div className="text-base">Aktion ({calculation.promotion.description})</div>
                        <div>
                            {!!calculation.estimated_price && !!calculation.promotion.discount_prefix && (
                                <Fragment>
                                    {t('garageList.promotion.discountPrefix.' + calculation.promotion.discount_prefix)} € <span className="text-3xl">{formatMoney(calculation.estimated_price)}</span>
                                </Fragment>
                            )}
                            {!!calculation.estimated_price && !calculation.promotion.discount_prefix && (
                                <Fragment>
                                    € <span className="text-3xl">{formatMoney(calculation.estimated_price)}</span>
                                </Fragment>
                            )}
                            {!calculation.estimated_price && (
                                <span className="text-3xl">Vor Ort</span>
                            )}
                        </div>
                    </div>
                </Fragment>
            )}
            <div className="mt-2 mb-3">
                {calculation.promotion && calculation.promotion.bonuses && calculation.promotion.bonuses.length > 0 && (
                    <GarageListItemBonuses bonuses={calculation.promotion.bonuses} shortDescription={true} headline={calculation.promotion.type !== 'bonus' ? '' : 'Hinweis'}/>
                )}
            </div>
            {!!calculation.estimated_price && labour && parts && labour.length > 0 && (
                <Fragment>
                    <div className={'mb-3 flex items-center gap-1 cursor-pointer'}
                         onClick={() => setCalculationDetailsOpened(true)}>
                        <InformationCircleIcon className={'h-4 w-4 text-primary'}/> Angebotsdetails
                        ansehen <ChevronRightIcon className={'h-4 w-4 text-primary'}/>
                    </div>
                    <Modal
                        title={"Kostenvoranschlag"}
                        open={calculationDetailsOpened}
                        width={'3xl'}
                        onClick={() => setCalculationDetailsOpened(false)}
                        onClose={() => setCalculationDetailsOpened(false)}>
                        <div className={'overflow-y-auto'}>
                            <div className={'text-lg font-bold flex gap-3 items-center mt-6 mb-4'}>
                                <LabourStepsIcon/> Arbeitsaufwand
                            </div>
                            <ul className="
                        grid md:grid-cols-2
                        list-disc marker:text-primary
                        text-left
                        mb-6">
                                {labour && labour.map((labourStep, idx) => (
                                    <li className={'mb-3 mr-3 ml-4'} key={idx}>
                                        {labourStep.name}
                                    </li>
                                ))}
                            </ul>

                            <div className={'text-lg font-bold flex gap-3 items-center mb-4'}>
                                <CogIcon/> Verwendete Materialien
                            </div>
                            <ul className="
                        grid md:grid-cols-2
                        list-disc marker:text-primary
                        text-left
                        mb-6">
                                {parts && parts.map((part, idx) => (
                                    <li className={'mb-3 mr-3 ml-4'} key={idx}>
                                        {part.name}
                                    </li>
                                ))}
                            </ul>
                            <Hr className={'mb-5'}/>
                            <div className={'flex justify-between font-bold text-success'}>
                                <div>
                                    Gesamtbetrag
                                </div>
                                <div className={'text-xl'}>
                                    &euro; {formatMoney(calculation.estimated_price)}
                                </div>
                            </div>
                        </div>
                    </Modal>
                </Fragment>
            )}
        </div>
    );
})
